import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { marketplace } from '../lib/api';

/**
 * Display-only currency switching. Sellers price in USD and everything settles in
 * USD; this context just converts the *displayed* amount using a served FX table.
 * The selected currency persists in localStorage so it survives reloads.
 */
const CurrencyContext = createContext(null);

const FALLBACK = { USD: { rate: 1, symbol: '$', name: 'US Dollar' } };

export function CurrencyProvider({ children }) {
  const [rates, setRates] = useState(FALLBACK);
  const [currency, setCurrencyState] = useState(() => localStorage.getItem('loreto_currency') || 'USD');

  useEffect(() => {
    let cancelled = false;
    marketplace.rates()
      .then((r) => {
        if (cancelled || !r?.rates) return;
        setRates(r.rates);
        // If a previously-selected currency is no longer offered, fall back to USD.
        setCurrencyState((cur) => {
          if (r.rates[cur]) return cur;
          localStorage.setItem('loreto_currency', 'USD');
          return 'USD';
        });
      })
      .catch(() => { /* keep USD-only fallback */ });
    return () => { cancelled = true; };
  }, []);

  const setCurrency = useCallback((c) => {
    setCurrencyState(c);
    localStorage.setItem('loreto_currency', c);
  }, []);

  const format = useCallback((usd) => {
    const amount = Number(usd) || 0;
    const meta = rates[currency] || FALLBACK.USD;
    const converted = amount * (meta.rate || 1);
    // No decimals for zero-decimal-ish currencies (JPY); two otherwise.
    const noDecimals = currency === 'JPY';
    const num = converted.toLocaleString(undefined, {
      minimumFractionDigits: noDecimals ? 0 : 2,
      maximumFractionDigits: noDecimals ? 0 : 2,
    });
    return `${meta.symbol || ''}${num}`;
  }, [rates, currency]);

  const value = useMemo(() => ({
    currency, setCurrency, rates, format,
    currencies: Object.keys(rates),
  }), [currency, setCurrency, rates, format]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used inside <CurrencyProvider>');
  return ctx;
}
